/**
 * Utility to extract the dominant color of an image from its URL.
 * It samples the image and finds the most frequent color bucket,
 * ignoring extreme whites and blacks to get a more "thematic" color.
 */
export const getDominantColor = (url: string): Promise<string> => {
    return new Promise((resolve) => {
        if (!url) {
            resolve('rgba(0,0,0,0.1)');
            return;
        }

        const img = new Image();
        img.crossOrigin = "Anonymous";
        
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                const size = 20; // Sample at 20x20 for better accuracy
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');
                
                if (ctx) {
                    ctx.drawImage(img, 0, 0, size, size);
                    const imageData = ctx.getImageData(0, 0, size, size).data;
                    const buckets: { [key: string]: number } = {};
                    let maxCount = 0;
                    let dominantColor = 'rgba(0,0,0,0.1)';

                    for (let i = 0; i < imageData.length; i += 4) {
                        const r = imageData[i];
                        const g = imageData[i + 1];
                        const b = imageData[i + 2];
                        const a = imageData[i + 3];

                        if (a < 128) continue; // Skip transparent pixels

                        // Ignore very bright or very dark pixels to get a better thematic color
                        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                        if (brightness > 245 || brightness < 10) continue;

                        // Bucket colors by rounding (32 levels per channel)
                        const bucketR = Math.round(r / 32) * 32;
                        const bucketG = Math.round(g / 32) * 32;
                        const bucketB = Math.round(b / 32) * 32;
                        const key = `${bucketR},${bucketG},${bucketB}`;

                        buckets[key] = (buckets[key] || 0) + 1;
                        if (buckets[key] > maxCount) {
                            maxCount = buckets[key];
                            dominantColor = `rgb(${bucketR}, ${bucketG}, ${bucketB})`;
                        }
                    }

                    // If no dominant color found (e.g. all white/black), fall back to 1x1 average
                    if (maxCount === 0) {
                        ctx.drawImage(img, 0, 0, 1, 1);
                        const d = ctx.getImageData(0, 0, 1, 1).data;
                        resolve(`rgb(${d[0]}, ${d[1]}, ${d[2]})`);
                    } else {
                        resolve(dominantColor);
                    }
                } else {
                    resolve('rgba(0,0,0,0.1)');
                }
            } catch (err) {
                // Fallback for CORS issues
                resolve('rgba(0,0,0,0.1)');
            }
        };

        img.onerror = () => {
            resolve('rgba(0,0,0,0.1)');
        };

        // Add a timestamp to bypass potential cache issues with CORS
        img.src = url + (url.includes('?') ? '&' : '?') + 't=' + new Date().getTime();
    });
};
